/**
 * Leave & Cover date arithmetic and RAG derivation — the client's half.
 *
 * A deliberate mirror of the same names in `functions/src/leave/leaveLogic.ts`.
 * The two workspaces build separately and neither can import the other, so the
 * request form would otherwise tell somebody their leave is routine when the
 * server is about to treat it as urgent, or paint a calendar entry a colour the
 * projection does not hold. `scripts/leave-test.mjs` imports both copies and
 * fails if they disagree, so drift is caught by `npm run verify` rather than by
 * a school whose urgent requests quietly stop bypassing the line manager.
 *
 * Keep this file free of anything the server half does not also have.
 */
import type { LeaveStatus, LeaveRag } from '../types';

export const LEAVE_DEFAULT_ESCALATION_DAYS = 2;
export const LEAVE_DEFAULT_URGENT_WINDOW_DAYS = 2;

export const LEAVE_MAX_SLT_APPROVERS = 10;
export const LEAVE_MAX_COVER_TEAM = 30;
export const LEAVE_MAX_PERIODS = 20;
export const LEAVE_MAX_LEAVE_TYPES = 20;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Europe/London school-day date as yyyy-mm-dd — the canonical date key across
 *  the app. Never use toISOString(), which is a day out for half the year. */
export function leaveDateKey(ms: number = Date.now()): string {
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

/** Today, as the school means it. */
export function leaveToday(): string {
  return leaveDateKey(Date.now());
}

function keyToUtcNoon(ymd: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return NaN;
  const t = Date.parse(`${ymd}T12:00:00Z`);
  if (isNaN(t)) return NaN;
  return leaveDateKey(t) === ymd ? t : NaN;
}

export function isValidDateKey(ymd: unknown): ymd is string {
  return typeof ymd === 'string' && !isNaN(keyToUtcNoon(ymd));
}

export function isWeekend(ymd: string): boolean {
  const dow = new Date(keyToUtcNoon(ymd)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** Working days between two date keys, Mon–Fri, excluding the start date.
 *  Negative when `toYmd` is in the past. */
export function workingDaysBetween(fromYmd: string, toYmd: string): number {
  const from = keyToUtcNoon(fromYmd);
  const to = keyToUtcNoon(toYmd);
  if (isNaN(from) || isNaN(to)) return NaN;
  if (from === to) return 0;

  const backwards = to < from;
  const start = backwards ? to : from;
  const end = backwards ? from : to;

  let count = 0;
  for (let t = start + DAY_MS; t <= end; t += DAY_MS) {
    if (!isWeekend(leaveDateKey(t))) count += 1;
  }
  return backwards ? -count : count;
}

export function addWorkingDays(fromYmd: string, n: number): string {
  let t = keyToUtcNoon(fromYmd);
  if (isNaN(t)) return fromYmd;
  let remaining = n;
  while (remaining > 0) {
    t += DAY_MS;
    if (!isWeekend(leaveDateKey(t))) remaining -= 1;
  }
  return leaveDateKey(t);
}

/** Close enough to skip the line manager and go straight to SLT. */
export function isUrgent(dateYmd: string, todayYmd: string, windowDays: number): boolean {
  const days = workingDaysBetween(todayYmd, dateYmd);
  if (isNaN(days)) return false;
  return days <= Math.max(0, windowDays);
}

/** The colour the cover team sees, or null when the request should not appear
 *  on their calendar at all. */
export function ragFor(status: LeaveStatus): LeaveRag | null {
  switch (status) {
    case 'withdrawn':
      return null;
    case 'declined':
      return 'red';
    case 'approved':
      return 'green';
    case 'pendingLineManager':
    case 'pendingSlt':
      return 'amber';
    default:
      return null;
  }
}

// ─── Display ─────────────────────────────────────────────────────────────────
// Client-only below this line; nothing here is mirrored on the server.

/** Badge classes per RAG, using the house `badge bg-{c}-100 text-{c}-700` pair. */
export const LEAVE_RAG_CLASSES: Record<LeaveRag, string> = {
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-green-100 text-green-700',
};

/** Calendar day-cell chips run a shade darker than badges, matching
 *  CAL_MODULE_META in schoolCalendar.ts. */
export const LEAVE_RAG_CHIP_CLASSES: Record<LeaveRag, string> = {
  red: 'bg-red-100 text-red-800',
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
};

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  pendingLineManager: 'With your line manager',
  pendingSlt: 'With SLT for sign-off',
  approved: 'Approved',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

export const LEAVE_STATUS_CHIPS: Record<LeaveStatus, string> = {
  pendingLineManager: 'bg-amber-100 text-amber-700',
  pendingSlt: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  withdrawn: 'bg-gray-100 text-gray-500',
};

/** A date key as "Mon, 3 Sep 2026". */
export function formatLeaveDate(ymd: string): string {
  const t = keyToUtcNoon(ymd);
  if (isNaN(t)) return ymd;
  return new Date(t).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

/** "Lesson 1, Lesson 2 and Break duty" — periods read as a sentence, because
 *  the cover team scans these far more often than they click into them. */
export function formatPeriodList(periods: { name: string }[]): string {
  const names = periods.map((p) => p.name);
  if (names.length === 0) return 'None';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
